import api, {
    PermissionType, Role, User, UserType
} from '@api';
import { FilterAlt } from '@mui/icons-material';
import { Tooltip, Typography } from '@mui/material';
import { ButtonBar, ButtonBarProps } from '@tsp-ui/core/components';
import { getFullName, useAsyncEffect, usePageMessage } from '@tsp-ui/core/utils';
import { useGetCurrentAccount } from '@utils/hooks';
import { useHasPermission } from '@utils/hooks/useHasPermission';
import {
    createContext, useCallback, useContext, useMemo, useState
} from 'react';

import AdminPageTemplate, {
    AdminEntityGroup, AdminEntityGroupProps, AdminPageContextValues, defaultAdminPageContextValues
} from '../components/AdminPageTemplate';

import { UserCard } from './UserCard';
import { UserForm } from './UserForm';


export const UserManagementPageContext = createContext<AdminPageContextValues<User>>(defaultAdminPageContextValues);
export const RolesContext = createContext<AdminPageContextValues<Role>>(defaultAdminPageContextValues);

export default function UserManagementPage() {
    const {
        accountUserType, customerId, id: clientID, name, customerName
    } = useGetCurrentAccount();
    const pageMessage = usePageMessage();
    const fetchUsers = useCallback(() => (
        api.users.getUsers(clientID, customerId)
    ), [ clientID, customerId ]);

    const [ roles, setRoles ] = useState<Role[]>([]);
    const [ roleFilter, setRoleFilter ] = useState('');

    const rolesContextValue = {
        entities: roles,
        setEntities: setRoles
    };

    const EntityGroupComponent = useMemo(() => (
        createUserGroup(roleFilter, name, customerName)
    ), [
        customerName, name, roleFilter
    ]);

    useAsyncEffect(useCallback(async () => {
        try {
            setRoles(await api.roles.getRoles(clientID, accountUserType));
        } catch (error) {
            pageMessage.handleApiError('An error occurred while fetching user roles', error);
        }
    }, [
        clientID, accountUserType, pageMessage
    ]));

    const [ canAddUser ] = useHasPermission([ PermissionType.CREATE_USERS ]);

    return (
        <RolesContext.Provider value={rolesContextValue}>
            <AdminPageTemplate
                disableAddEntity={!canAddUser}
                disabledAddTooltip="You don't have permission to add a user"
                Context={UserManagementPageContext}
                CreateEditForm={UserForm}
                entityName="user"
                EntityGroupComponent={EntityGroupComponent}
                fetchEntities={fetchUsers}
                filterByLabel="name or email"
                filterEntity={(
                    (entities, filterInputValue) => filterUser(entities, filterInputValue, roles, roleFilter)
                )}
                justifyFilters="flex-start"
                otherFilters={(
                    <ButtonBar
                        label="Filter by role"
                        onValueChange={newValue => setRoleFilter(newValue)}
                        optional
                        options={roles.reduce<ButtonBarProps['options']>((prev, { name }) => ({
                            ...prev,
                            [name]: name
                        }), {})}
                    />
                )}
                visibleGroups={[ accountUserType ]}
            />
        </RolesContext.Provider>
    );
}

function createUserGroup(roleFilter: string, name: string, customerName?: string) {
    return function UserGroup({
        entities,
        group: userType
    }: AdminEntityGroupProps<User, UserType>) {
        const users = entities.filter((entity) => entity.type === userType);
        const { setEntities: setUsers } = useContext(UserManagementPageContext);

        return (
            <AdminEntityGroup
                header={(
                    <>
                        {`${userType === 'CLIENT' ? name : customerName} users`}

                        {roleFilter && (
                            <Tooltip title="Filters active">
                                <FilterAlt color="primary" />
                            </Tooltip>
                        )}
                    </>
                )}
            >
                {users.map((user) => (
                    <UserCard
                        key={user.id}
                        user={user}
                        setUsers={setUsers}
                    />
                ))}

                {users.length === 0 && (
                    <Typography>
                        No {userType.toLowerCase()} users found
                    </Typography>
                )}
            </AdminEntityGroup>
        );
    };
}

function filterUser(user: User, debouncedTerm: string, roles: Role[], roleNameFilter?: string) {
    const { email, roleIds } = user;

    const filteredRoleID = roles.find(role => role.name === roleNameFilter)?.id;

    const roleIncluded = !roleNameFilter || (!!filteredRoleID && roleIds.includes(filteredRoleID));

    return roleIncluded && (
        getFullName(user).toLocaleLowerCase().includes(debouncedTerm)
        || email.toLocaleLowerCase().includes(debouncedTerm)
    );
}
